/**
 * PATH: packages/ui/src/kanban/components/placeBesideTrigger.ts
 * FILE_ID: UI-KANBAN-PLACE-BESIDE-TRIGGER-001
 * PURPOSE: Place a portaled card-action popover beside its trigger icon.
 * WHY: Bottom-anchored pickers + viewport clamp shoved tall menus (Due /
 *   Assign) up over the action rail, hiding the clicked icon. Side
 *   placement (same contract as KanbanCardHoverMenu Colour/Labels) keeps
 *   the trigger and toolbar visible.
 * FLOW:
 *   trigger rect + menu size → prefer right → flip left → vertical clamp
 */

export interface TriggerRect {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

export interface MenuSize {
  width: number
  height: number
}

export interface PlaceBesideResult {
  left: number
  top: number
  /** Which side of the trigger the menu opened on (for hover aprons). */
  side: 'left' | 'right'
}

/**
 * f(trigger, menu, viewport?) → fixed {left, top, side}.
 * Pure I→O — never mutates inputs. Uses window when viewport omitted.
 */
export function placeBesideTrigger(
  trigger: TriggerRect,
  menu: MenuSize,
  opts?: {
    gap?: number
    margin?: number
    viewport?: { width: number; height: number }
  },
): PlaceBesideResult {
  const gap = opts?.gap ?? 8
  const margin = opts?.margin ?? 8
  const vw = opts?.viewport?.width ?? (typeof window !== 'undefined' ? window.innerWidth : 1024)
  const vh = opts?.viewport?.height ?? (typeof window !== 'undefined' ? window.innerHeight : 768)

  const preferRight = trigger.right + gap + menu.width + margin <= vw
  const preferLeft = trigger.left - gap - menu.width >= margin

  let side: 'left' | 'right' = 'right'
  let left: number
  if (preferRight || !preferLeft) {
    side = 'right'
    left = trigger.right + gap
  } else {
    side = 'left'
    left = trigger.left - gap - menu.width
  }
  left = Math.max(margin, Math.min(left, vw - menu.width - margin))

  // Vertically centre on the trigger so the icon stays visually linked;
  // clamp to the viewport without yanking the menu over the rail.
  let top = trigger.top + trigger.height / 2 - menu.height / 2
  if (menu.height + margin * 2 > vh) {
    top = margin
  } else {
    top = Math.max(margin, Math.min(top, vh - menu.height - margin))
  }

  return { left, top, side }
}

/** Capture a trigger element's viewport rect for placeBesideTrigger. */
export function triggerRectFromElement(el: Element): TriggerRect {
  const r = el.getBoundingClientRect()
  return {
    left: r.left,
    top: r.top,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height,
  }
}
